import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography, Button } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { HallMaintenanceTask } from '../../domain/HallMaintenanceTask';
import { withLazyMounting } from '../../components/hoc/LazyDialog';

interface Props {
    isOpen: boolean;
    task: HallMaintenanceTask;
    onClose: () => void;
}

const HallMaintenanceTaskViewDialog = (props: Props) => {
    const { t } = useTranslation();
    const months = moment.monthsShort();
    const task = props.task;

    const formatFrequency = (task: HallMaintenanceTask) => {
        if (task.frequencyLabel) return task.frequencyLabel;
        return `${task.frequencyInterval} ${t(task.frequencyUnit)}`;
    };

    return (
        <Dialog open={props.isOpen} fullWidth maxWidth="md" onClose={props.onClose}>
            <DialogTitle>{`#${task.cardNumber} - ${task.title}`}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="subtitle1" color="text.secondary">
                        {`${t("category")}: ${t(task.category)}`} <br />
                        {task.subTitle} <br />
                        {`${t("frequency")}: ${formatFrequency(task)}`}
                    </Typography>
                    <Divider />
                    {task.warningText && (
                        <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                            {task.warningText}
                        </Alert>
                    )}
                    <Divider />
                    {task.description && (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {task.description}
                        </Typography>
                    )}
                    {task.plannedMonths && task.plannedMonths.length > 0 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                gap: 0.5
                            }}
                        >
                            {Array.from({ length: 12 }).map((_, idx) => {
                                const monthNumber = idx + 1;
                                const isPlanned = task.plannedMonths!.includes(monthNumber);
                                return (
                                    <Box
                                        key={monthNumber}
                                        sx={{
                                            textAlign: 'center',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: isPlanned ? 'primary.main' : 'divider',
                                            bgcolor: isPlanned ? 'primary.light' : 'background.paper',
                                            color: isPlanned ? 'primary.contrastText' : 'text.primary',
                                            py: 0.25,
                                            fontWeight: isPlanned ? 700 : 500,
                                        }}
                                    >
                                        {months[idx]}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>{t("close")}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default withLazyMounting(HallMaintenanceTaskViewDialog);